import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Phone, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { toast } = useToast();
  const { language, t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_type: 'kontakt',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        }),
      });
      
      if (response.ok) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Fehler');
      }
    } catch (error) {
      toast({
        title: t('fehler'),
        description: language === 'en' ? 'Please try again.' : 'Bitte versuchen Sie es erneut.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="kontakt" className="py-20 bg-gradient-to-br from-gray-50 to-sky-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Contact ' : 'Kontaktieren Sie '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">
              {language === 'en' ? 'Us' : 'uns'}
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' 
              ? 'Get free and non-binding advice'
              : 'Lassen Sie sich kostenlos und unverbindlich beraten'}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {language === 'en' ? 'We are here for you' : 'Wir sind für Sie da'}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                {language === 'en' 
                  ? 'Our expert team is available to answer all your questions about photovoltaics and heat pumps. Contact us for a free initial consultation.'
                  : 'Unser Expertenteam steht Ihnen bei allen Fragen rund um Photovoltaik und Wärmepumpen zur Verfügung. Kontaktieren Sie uns für eine kostenlose Erstberatung.'}
              </p>
            </div>

            <div className="space-y-6">
              <Card className="border-0 bg-white hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex items-start">
                  <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Phone className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('telefon')}</h4>
                    <p className="text-gray-600">+49 157 82283963</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {language === 'en' ? 'Mon-Fri: 8:00 - 18:00' : 'Mo-Fr: 8:00 - 18:00 Uhr'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 bg-white hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex items-start">
                  <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Mail className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('email')}</h4>
                    <p className="text-gray-600">Buchhaltung@gravante.de</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {language === 'en' ? 'Response within 24h' : 'Antwort innerhalb 24h'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 bg-white hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex items-start">
                  <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('adresse')}</h4>
                    <p className="text-gray-600">Am Wammesknopf 6</p>
                    <p className="text-gray-600">70439 Stuttgart, {language === 'en' ? 'Germany' : 'Deutschland'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-0 bg-white shadow-xl">
            <CardContent className="p-8">
              {isSuccess ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {language === 'en' ? 'Thank you!' : 'Vielen Dank!'}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {language === 'en' 
                      ? 'Your request has been sent successfully. We will contact you as soon as possible.'
                      : 'Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns schnellstmöglich bei Ihnen.'}
                  </p>
                  <Button 
                    onClick={() => setIsSuccess(false)}
                    variant="outline"
                    className="border-sky-600 text-sky-600 hover:bg-sky-50"
                  >
                    {language === 'en' ? 'Send new request' : 'Neue Anfrage senden'}
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {language === 'en' ? 'Send Request' : 'Anfrage senden'}
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('name')} *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={language === 'en' ? 'Your name' : 'Ihr Name'}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('email')} *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={language === 'en' ? 'your@email.com' : 'ihre@email.de'}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('telefon')}
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+49 123 456 789"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('nachricht')} *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder={language === 'en' ? 'Describe your project or questions...' : 'Beschreiben Sie Ihr Projekt oder Ihre Fragen...'}
                        className="w-full"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg group disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {language === 'en' ? 'Sending...' : 'Wird gesendet...'}
                        </>
                      ) : (
                        <>
                          {language === 'en' ? 'Send Request' : 'Anfrage senden'}
                          <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
